// SSE Client — fetch + ReadableStream parser for server-sent events
// Adapted from signal-chat/frontend/lib/api.ts pattern

import type { SSEEvent } from "../../../shared/types.ts";
import { apiUrl } from "./api";
import type { AgentErrorCode } from "../../../shared/types.ts";
import type { BrandReadinessMissingKey } from "../../../shared/types.ts";

export type AgentStreamErrorState = {
  code: AgentErrorCode;
  message: string;
  retryable: boolean;
};

export interface SSECallbacks {
  onDelta?: (content: string) => void;
  onToolStart?: (id: string, name: string, input: string) => void;
  onToolEnd?: (id: string, name: string, status: "success" | "failed", output: string) => void;
  onToolProgress?: (id: string, name: string, message: string) => void;
  onSynthesisLearned?: (executionId: string, summary: string, ok: boolean) => void;
  onArtifact?: (artifact: { id: string; name: string; content_type: string; preview: string | null; key: string }) => void;
  onStatus?: (status: string, detail?: string) => void;
  onBrandNotReady?: (missing: BrandReadinessMissingKey[]) => void;
  onDone?: (messageId: string) => void;
  onError?: (error: AgentStreamErrorState) => void;
}

type ProgressTraceRow = {
  type: "status" | "tool_start" | "tool_end" | "tool_progress" | "done" | "error";
  browser_received_at_ms: number;
  server_emitted_at_ms: number | null;
  stream_id: number;
  read_batch: number;
  id?: string;
  name?: string;
  status?: "success" | "failed";
};

let nextTraceStreamId = 0;

/** Opt-in, bounded browser timing evidence; never stores prompts or tool payloads. */
function captureProgressTrace(event: SSEEvent, streamId: number, readBatch: number, browserReceivedAtMs: number): void {
  if (event.type !== "status" && event.type !== "tool_start" && event.type !== "tool_end"
    && event.type !== "tool_progress" && event.type !== "done" && event.type !== "error") return;
  if (typeof window === "undefined" || new URLSearchParams(window.location.search).get("agentTrace") !== "1") return;

  const traceWindow = window as typeof window & { __hcAgentProgressTrace?: ProgressTraceRow[] };
  const row: ProgressTraceRow = {
    type: event.type,
    browser_received_at_ms: browserReceivedAtMs,
    server_emitted_at_ms: typeof event.server_emitted_at_ms === "number" ? event.server_emitted_at_ms : null,
    stream_id: streamId,
    read_batch: readBatch,
  };
  if (event.type === "tool_start" || event.type === "tool_end" || event.type === "tool_progress") {
    row.id = event.id;
    row.name = event.name;
  }
  if (event.type === "tool_end") row.status = event.status;
  traceWindow.__hcAgentProgressTrace = [...(traceWindow.__hcAgentProgressTrace ?? []), row].slice(-64);
}

/**
 * POST a message and stream SSE events from the response.
 * Uses fetch + ReadableStream (not EventSource, which is GET-only).
 */
export async function streamSSE(
  url: string,
  body: Record<string, unknown>,
  callbacks: SSECallbacks,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(apiUrl(url), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: "Request failed" } }));
    callbacks.onError?.({
      code: "unknown",
      message: err.error?.message || `HTTP ${res.status}`,
      retryable: true,
    });
    return;
  }

  const reader = res.body?.getReader();
  if (!reader) {
    callbacks.onError?.({ code: "unknown", message: "No response body", retryable: true });
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let receivedTerminalEvent = false;
  const streamId = ++nextTraceStreamId;
  let readBatch = 0;
  let batchReceivedAtMs = 0;
  const dispatch = (event: SSEEvent) => {
    if (receivedTerminalEvent) return;
    captureProgressTrace(event, streamId, readBatch, batchReceivedAtMs);
    if (event.type === "done" || event.type === "error") receivedTerminalEvent = true;
    dispatchEvent(event, callbacks);
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      batchReceivedAtMs = Date.now();
      readBatch += 1;

      buffer += decoder.decode(value, { stream: true });

      // Split on double newline (SSE event boundary)
      const parts = buffer.split("\n\n");
      // Keep the last incomplete part in the buffer
      buffer = parts.pop() || "";

      for (const part of parts) {
        if (!part.trim()) continue;
        // Skip comment lines (keepalive pings)
        if (part.startsWith(":")) continue;
        // Skip [DONE] sentinel
        if (part.includes("[DONE]")) continue;

        const dataLine = part
          .split("\n")
          .find((line) => line.startsWith("data: "));
        if (!dataLine) continue;

        try {
          const event: SSEEvent = JSON.parse(dataLine.slice(6));
          dispatch(event);
        } catch {
          // Malformed JSON — skip
        }
      }
    }

    // Flush trailing buffer — last SSE event may not end with \n\n before stream closes
    if (buffer.trim()) {
      const tailParts = buffer.split("\n\n");
      for (const part of tailParts) {
        if (!part.trim()) continue;
        if (part.startsWith(":")) continue;
        if (part.includes("[DONE]")) continue;
        const dataLine = part.split("\n").find((line) => line.startsWith("data: "));
        if (!dataLine) continue;
        try {
          const event: SSEEvent = JSON.parse(dataLine.slice(6));
          dispatch(event);
        } catch {
          // Malformed JSON — skip
        }
      }
    }
    if (!receivedTerminalEvent && !signal?.aborted) {
      callbacks.onError?.({
        code: "agent_error",
        message: "The agent connection closed before a result was received. Please try again.",
        retryable: true,
      });
    }
  } catch (err) {
    if (signal?.aborted || receivedTerminalEvent) return; // Cancellation or already reported
    callbacks.onError?.({
      code: "unknown",
      message: err instanceof Error ? err.message : "Stream error",
      retryable: true,
    });
  }
}

function dispatchEvent(event: SSEEvent, callbacks: SSECallbacks): void {
  switch (event.type) {
    case "delta":
      callbacks.onDelta?.(event.content);
      break;
    case "tool_start":
      callbacks.onToolStart?.(event.id, event.name, event.input);
      break;
    case "tool_end":
      callbacks.onToolEnd?.(event.id, event.name, event.status, event.output);
      break;
    case "tool_progress":
      callbacks.onToolProgress?.(event.id, event.name, event.message);
      break;
    case "synthesis_learned":
      callbacks.onSynthesisLearned?.(event.execution_id, event.summary, event.ok);
      break;
    case "artifact":
      callbacks.onArtifact?.(event);
      break;
    case "status":
      callbacks.onStatus?.(event.status, event.detail);
      break;
    case "brand_not_ready":
      callbacks.onBrandNotReady?.(event.missing);
      break;
    case "done":
      callbacks.onDone?.(event.message_id);
      break;
    case "error":
      callbacks.onError?.({
        code: event.code,
        message: event.message,
        retryable: event.retryable,
      });
      break;
  }
}
